"use client";

import * as React from "react";
import {
  CalendarDays,
  Check,
  Clock,
  Copy,
  HardDrive,
  Info,
  Wifi,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { daysLeft, faDate, faNum, toFa } from "@/lib/format";

export type Plan = {
  id: string;
  name: string;
  description: string;
  priceToman: number;
  durationDays: number;
  trafficGB: number;
  popular: boolean;
  active: boolean;
  sortOrder: number;
};

export type Subscription = {
  id: string;
  startDate: string;
  endDate: string;
  trafficLimitGB: number;
  vlessLink: string | null;
  uuid: string | null;
  status: "ACTIVE" | "EXPIRED" | "SUSPENDED";
  createdAt: string;
  plan: Plan;
  payment?: unknown;
};

function durationLabel(days: number): string {
  if (days >= 60) return `${faNum(Math.round(days / 30))} ماهه`;
  return `${faNum(days)} روزه`;
}

function trafficLabel(gb: number): string {
  return gb === 0 ? "نامحدود" : `${faNum(gb)} گیگابایت`;
}

export function VpnConfigCard({ subscription }: { subscription: Subscription }) {
  const [copied, setCopied] = React.useState(false);
  const { plan, vlessLink, status, startDate, endDate, trafficLimitGB } =
    subscription;

  const isActive = status === "ACTIVE";
  const left = daysLeft(endDate);
  const expired = !isActive || left <= 0;

  // Time-remaining percentage (clamped 0..100)
  const totalMs = new Date(endDate).getTime() - new Date(startDate).getTime();
  const elapsedMs = Date.now() - new Date(startDate).getTime();
  const remainingPct =
    totalMs > 0
      ? Math.min(100, Math.max(0, ((totalMs - elapsedMs) / totalMs) * 100))
      : 0;

  const copyLink = async () => {
    if (!vlessLink) return;
    try {
      await navigator.clipboard.writeText(vlessLink);
      setCopied(true);
      toast.success("کپی شد!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("کپی ناموفق بود");
    }
  };

  const statusBadge = isActive ? (
    <Badge className="border-emerald-500/30 bg-emerald-500/15 text-emerald-500">
      فعال
    </Badge>
  ) : status === "SUSPENDED" ? (
    <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-500">
      معلق
    </Badge>
  ) : (
    <Badge variant="destructive" className="border-red-500/30 bg-red-500/15 text-red-500">
      منقضی
    </Badge>
  );

  return (
    <article className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold leading-tight">{plan.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {durationLabel(plan.durationDays)} • {trafficLabel(trafficLimitGB)}
          </p>
        </div>
        {statusBadge}
      </header>

      <Separator className="my-4" />

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Stat
          icon={<CalendarDays className="size-4 text-primary" />}
          label="شروع"
          value={faDate(startDate)}
        />
        <Stat
          icon={<CalendarDays className="size-4 text-primary" />}
          label="انقضا"
          value={faDate(endDate)}
        />
        <Stat
          icon={<HardDrive className="size-4 text-primary" />}
          label="ترافیک"
          value={trafficLabel(trafficLimitGB)}
        />
        <Stat
          icon={<Clock className="size-4 text-primary" />}
          label="باقی‌مانده"
          value={expired ? "منقضی شده" : `${faNum(left)} روز`}
        />
      </div>

      {!expired && (
        <div className="mt-4">
          <Progress value={remainingPct} className="h-2" />
          <p className="mt-1.5 text-xs text-muted-foreground">
            {toFa(Math.round(remainingPct))}٪ از مدت اشتراک باقی است
          </p>
        </div>
      )}

      <Separator className="my-4" />

      {vlessLink ? (
        <section className="space-y-3" aria-label="کانفیگ اتصال">
          <div className="flex items-center gap-2">
            <Wifi className="size-4 text-primary" />
            <h4 className="text-sm font-semibold">کانفیگ اتصال (VLESS)</h4>
          </div>
          <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1 space-y-2">
              <div
                dir="ltr"
                className="scrollbar-thin max-h-24 select-all overflow-auto rounded-xl border bg-muted/50 p-3 font-mono text-xs text-muted-foreground"
              >
                {vlessLink}
              </div>
              <Button
                onClick={copyLink}
                variant="outline"
                size="sm"
                className="w-full gap-2"
                aria-label="کپی لینک کانفیگ"
              >
                {copied ? (
                  <Check className="size-4 text-primary" />
                ) : (
                  <Copy className="size-4" />
                )}
                {copied ? "کپی شد" : "کپی"}
              </Button>
            </div>
            <div className="bg-white p-3 self-center rounded-xl">
              <QRCodeSVG value={vlessLink} size={180} />
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            این لینک را در اپلیکیشن V2rayNG / Hiddify / Streisand وارد کنید.
          </p>
        </section>
      ) : (
        <p className="text-sm text-muted-foreground">
          کانفیگ به‌زودی فعال می‌شود
        </p>
      )}
    </article>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary/10">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}

export default VpnConfigCard;
